import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Trash2 } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { ScreenHead, ListCard, Skeletons, Empty, PS_INPUT, titleCase, useClients, useProviders } from "./psPrimitives";
import { useClient } from "@/lib/clientContext";
import { ConfirmSheet } from "./ProvidersScreen";

// ---- shared Client selection across Practice Setup steps ----
let _cid = sessionStorage.getItem("revq.ps.cid") || "";
const subs = new Set();
export const setPsClient = (v) => setCidGlobal(String(v));
const setCidGlobal = (v) => { _cid = v; sessionStorage.setItem("revq.ps.cid", v); subs.forEach((f) => f()); };
export function usePsClient() {
  const { data: clients = [], isLoading } = useClients();
  const { selectedClientId } = useClient();
  const cid = useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => _cid);
  useEffect(() => {
    const has = (id) => id && clients.some((c) => String(c.client_id) === String(id));
    // Practice Setup always follows the globally selected client (login / sidebar picker).
    if (has(selectedClientId)) { if (String(selectedClientId) !== cid) setCidGlobal(String(selectedClientId)); return; }
    if (clients.length && !has(cid)) setCidGlobal(String(clients[0].client_id));
  }, [clients, cid, selectedClientId]);
  return { cid, setCid: setCidGlobal, clients, isLoading, client: clients.find((c) => String(c.client_id) === cid) };
}
// Providers scoped to the client in context — orphan NPIs (no client linked) always show.
export function useClientProviders() {
  const q = useProviders();
  const { cid } = usePsClient();
  const data = useMemo(() => (q.data || []).filter((p) => !p.client_id || String(p.client_id) === String(cid)), [q.data, cid]);
  return { ...q, data };
}
export function ClientPicker({ cid, setCid, clients, right }) {
  // Locked to the client in context — only that client is selectable here.
  const shown = clients.filter((c) => String(c.client_id) === String(cid));
  return (
    <div className="flex items-center gap-2.5 mb-4 flex-wrap">
      <label className="flex items-center gap-2.5">
        <span className="text-[11px] font-semibold text-[var(--gray2)]">Client</span>
        <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="ps-client-select"
          className="ctl-select">
          {shown.map((c) => <option key={c.client_id} value={String(c.client_id)}>{c.client_id}{c.name || c.description ? ` · ${c.name || c.description}` : ""}</option>)}
        </select>
      </label>
      {right ? <><span className="flex-1" />{right}</> : null}
    </div>
  );
}

// ---- master data ----
export const masterKey = (entity, cid) => ["master", entity, cid];
export function useMaster(entity, cid) {
  return useQuery({
    queryKey: masterKey(entity, cid), enabled: !!cid, staleTime: 15_000,
    queryFn: async () => (await api.get(`/api/edi/data-migration/config/${entity}?client_id=${encodeURIComponent(cid)}`)).data?.records ?? [],
  });
}
export const BTN2 = "text-[12.5px] font-semibold px-3.5 py-1.5 rounded-full border border-black/[.14] bg-white text-[var(--ink)] hover:bg-black/[.03] transition disabled:opacity-40";
export const BTN1 = "text-[12.5px] font-bold px-3.5 py-1.5 rounded-full text-white bg-[var(--ink)] hover:opacity-90 transition disabled:opacity-40";
export const LINK = "text-[12px] font-semibold text-[#4A4AC2] hover:underline";
const ERR = "mt-2 text-[11.5px] font-semibold text-[#B3261E]";
const fmtAddr = (a) => (!a ? "" : typeof a === "string" ? a : [a.address_1, a.address_2, a.city, [a.state, a.postal_code ? String(a.postal_code).slice(0, 5) : ""].filter(Boolean).join(" ")].filter(Boolean).join(", "));
// Common CMS place-of-service codes → friendly label (shown next to the code).
const POS_LABELS = { "02": "Telehealth", "10": "Telehealth at home", "11": "Office", "12": "Home", "19": "Off-campus outpatient hospital", "20": "Urgent care", "21": "Inpatient hospital", "22": "On-campus outpatient hospital", "23": "Emergency room", "24": "Ambulatory surgical center", "31": "Skilled nursing facility", "32": "Nursing facility", "49": "Independent clinic", "50": "FQHC", "71": "Public health clinic", "81": "Independent lab" };
const posText = (c) => { if (!c) return ""; const code = String(c).trim().padStart(2, "0"); return POS_LABELS[code] ? `POS ${code} · ${POS_LABELS[code]}` : `POS ${code}`; };

// fields: [{ k, label, required, npi, upper, wide }]
function MasterForm({ fields, initial, submitLabel, onSubmit, onCancel, validate, enrich }) {
  const [v, setV] = useState(() => ({ ...initial }));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const refs = useRef({});
  const typed = useRef(new Set(Object.keys(initial).filter((k) => initial[k])));
  useEffect(() => { refs.current[fields[0].k]?.focus(); }, []); // eslint-disable-line
  const set = (k, val) => { typed.current.add(k); setV((s) => ({ ...s, [k]: val })); setErr(""); };
  const npiF = fields.find((f) => f.npi);
  useEffect(() => {
    if (!enrich || !npiF) return;
    const n = String(v[npiF.k] || "").replace(/\D/g, "");
    if (n.length !== 10) return;
    api.post("/api/edi/note-to-claim/providers/lookup", { npi_number: n }).then(({ data }) => {
      const fill = { name: titleCase(data?.provider_name || ""), address: fmtAddr(data?.address) };
      setV((s) => { const nx = { ...s }; Object.entries(fill).forEach(([k, val]) => { if (val && fields.some((f) => f.k === k) && !String(s[k] || "").trim() && !typed.current.has(k)) nx[k] = val; }); return nx; });
    }).catch(() => {});
  }, [v[npiF?.k]]); // eslint-disable-line
  const submit = async () => {
    for (const f of fields) if (f.required && !String(v[f.k] || "").trim()) { setErr(`Add a ${f.label.toLowerCase()}.`); refs.current[f.k]?.focus(); return; }
    if (npiF && String(v[npiF.k] || "").trim() && !/^\d{10}$/.test(String(v[npiF.k]).trim())) { setErr("An NPI has 10 digits."); refs.current[npiF.k]?.focus(); return; }
    const badMoney = fields.find((f) => f.money && String(v[f.k] || "").trim() && !/^\$?\s*\d{1,3}(,?\d{3})*(\.\d{1,2})?$/.test(String(v[f.k]).trim()));
    if (badMoney) { setErr(`Enter the ${badMoney.label.toLowerCase()} as a number, like 128.50.`); refs.current[badMoney.k]?.focus(); return; }
    const clean = Object.fromEntries(fields.map((f) => { const s = String(v[f.k] || "").trim(); return [f.k, f.upper ? s.toUpperCase() : s]; }));
    const dup = validate(clean); if (dup) { setErr(dup); return; }
    setBusy(true);
    try { await onSubmit(clean); } catch (e) { setErr(describeError(e)); setBusy(false); }
  };
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } else if (e.key === "Escape") { e.preventDefault(); onCancel(); } };
  return (
    <div className="rounded-[14px] border border-[#E9E9EC] bg-[#FBFBFD] p-4 mb-3" data-testid="ps-master-form">
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(fields.length, 3)}, minmax(0,1fr))` }}>
        {fields.map((f) => (
          <label key={f.k} className={`block ${f.wide ? "col-span-full" : ""}`}>
            <span className="block text-[11px] font-semibold text-[var(--gray2)] mb-1">{f.label}{f.required ? "" : " · optional"}</span>
            <input ref={(el) => { refs.current[f.k] = el; }} value={v[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} onKeyDown={onKey}
              inputMode={f.npi ? "numeric" : f.money ? "decimal" : undefined} placeholder={f.placeholder} className={`${PS_INPUT} ${f.upper || f.money ? "font-mono" : ""}`} data-testid={`ps-mf-${f.k}`} />
          </label>
        ))}
      </div>
      {enrich && npiF ? <div className="text-[11px] text-[var(--gray2)] mt-2">Enter an NPI and revQ fills in the rest from the NPI registry.</div> : null}
      {err ? <div className={ERR} data-testid="ps-mf-error">{err}</div> : null}
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" className={BTN2} onClick={onCancel} data-testid="ps-mf-cancel">Cancel</button>
        <button type="button" className={BTN1} onClick={submit} disabled={busy} data-testid="ps-mf-submit">{submitLabel}</button>
      </div>
    </div>
  );
}

// One list component per master entity.
export function MasterList({ entity, cid, singular, plural, fields, titleOf, metaOf, toRecord, fromRecord, dupKey, headerNote, extraActions, above, testid, enrich, mono, groupOf, rankOf, emptyNode, rateOf, fmtRate, onSaved }) {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useMaster(entity, cid);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState(null); // null | "add" | record id
  const [rm, setRm] = useState(null);
  const [rmBusy, setRmBusy] = useState(false);
  useEffect(() => { setMode(null); setQ(""); }, [cid]);
  const refresh = () => qc.invalidateQueries({ queryKey: masterKey(entity, cid) });
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? rows.filter((r) => `${titleOf(r)} ${metaOf(r)}`.toLowerCase().includes(s)) : rows;
    if (!groupOf) return list;
    return [...list].sort((a, b) => rankOf(groupOf(a)) - rankOf(groupOf(b)) || String(titleOf(a)).localeCompare(String(titleOf(b)), undefined, { numeric: true }));
  }, [rows, q, titleOf, metaOf, groupOf, rankOf]);
  const gCount = useMemo(() => (groupOf ? shown.reduce((m, r) => { const g = groupOf(r); m[g] = (m[g] || 0) + 1; return m; }, {}) : {}), [shown, groupOf]);
  const validate = (exceptId) => (clean) => {
    const k = dupKey(clean); if (!k) return "";
    const hit = rows.find((r) => r.id !== exceptId && dupKey(fromRecord(r)) === k);
    return hit ? `${titleOf(toRecord(clean))} is already on this client.` : "";
  };
  const add = async (clean) => { await api.post(`/api/edi/data-migration/config/${entity}`, { client_id: cid, record: toRecord(clean), enrich: !!enrich }); if (onSaved) await onSaved(clean, null); setMode(null); refresh(); };
  const save = (id) => async (clean) => { const prev = rows.find((r) => r.id === id); await api.patch(`/api/edi/data-migration/config/${entity}/${id}`, { client_id: cid, record: toRecord(clean), ...toRecord(clean) }); if (onSaved) await onSaved(clean, prev); setMode(null); refresh(); };
  const doRemove = async () => { setRmBusy(true); try { await api.delete(`/api/edi/data-migration/config/${entity}/${rm.id}`, { params: { client_id: cid } }); setRm(null); refresh(); } catch (e) { alert(describeError(e)); } setRmBusy(false); };
  const empty = Object.fromEntries(fields.map((f) => [f.k, ""]));
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
        <span className="text-[13px] font-semibold text-[var(--gray2)]" data-testid={`${testid}-count`}>{rows.length} {rows.length === 1 ? singular : plural}{headerNote ? ` · ${headerNote}` : ""}</span>
        <span className="flex-1" />
        {extraActions}
        <button type="button" className={BTN2} onClick={() => setMode("add")} data-testid={`${testid}-add`}>Add {singular}</button>
      </div>
      {above}
      {rows.length > 6 ? (
        <div className="relative mb-3"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gray3)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${plural}…`} className={`${PS_INPUT} pl-9`} data-testid={`${testid}-search`} /></div>
      ) : null}
      {mode === "add" ? <MasterForm fields={fields} initial={empty} submitLabel={`Add ${singular}`} onSubmit={add} onCancel={() => setMode(null)} validate={validate(null)} enrich={enrich} /> : null}
      {isLoading ? <Skeletons /> : !rows.length ? (mode === "add" ? null : <Empty>{emptyNode || `No ${plural} yet.`}</Empty>) : (
        <ListCard testid={`${testid}-rows`}>
          {shown.map((r, i) => <React.Fragment key={r.id}>
            {groupOf && (i === 0 || groupOf(shown[i - 1]) !== groupOf(r)) ? (
              <div className={`flex items-center px-4 py-1.5 bg-[#F4F4F6] text-[11.5px] font-semibold text-[var(--gray2)] ${i ? "border-t border-[#E9E9EC]" : ""}`} data-testid={`${testid}-sep-${groupOf(r)}`}>
                <span className="flex-1">{groupOf(r)}</span><span className="tabular-nums">{gCount[groupOf(r)]}</span>
              </div>) : null}
            {mode === r.id ? (
            <div key={r.id} className="p-2"><MasterForm fields={fields} initial={fromRecord(r)} submitLabel="Save" onSubmit={save(r.id)} onCancel={() => setMode(null)} validate={validate(r.id)} enrich={false} /></div>
          ) : (
            <div key={r.id} className={`flex items-center gap-3 px-4 ${groupOf ? "py-2.5" : "py-3"} ${i || groupOf ? "border-t border-[#E9E9EC]" : ""}`} data-testid={`${testid}-row-${r.id}`} data-code={r.code || undefined}>
              {groupOf ? <>
                <span className="w-[76px] flex-none font-mono text-[12.5px] font-semibold text-[var(--ink)]">{titleOf(r)}</span>
                <span className="flex-1 min-w-0 truncate text-[12.5px] text-[var(--ink2)]" title={metaOf(r)}>{metaOf(r)}</span>
                {rateOf ? (rateOf(r) != null ? <span className="w-[92px] flex-none text-right font-mono text-[12.5px] tabular-nums text-[var(--ink)]" data-testid={`${testid}-rate-${r.id}`}>{fmtRate(rateOf(r))}</span>
                  : <span className="w-[92px] flex-none text-right text-[12px] text-[var(--gray3)]" data-testid={`${testid}-rate-${r.id}`}>No rate</span>) : null}
              </> : <div className="flex-1 min-w-0">
                <div className={`text-[13px] font-semibold text-[var(--ink)] truncate ${mono ? "font-mono" : ""}`}>{titleOf(r)}</div>
                <div className="text-[11.5px] text-[var(--gray2)] mt-0.5 truncate">{metaOf(r)}</div>
              </div>}
              <button type="button" className={LINK} onClick={() => setMode(r.id)} data-testid={`${testid}-edit-${r.id}`}>Edit</button>
              <button type="button" aria-label={`Remove ${titleOf(r)}`} onClick={() => setRm(r)} className="p-1.5 rounded-[8px] text-[var(--gray3)] hover:text-[#B3261E] hover:bg-black/[.04]" data-testid={`${testid}-remove-${r.id}`}><Trash2 size={14} /></button>
            </div>
          )}</React.Fragment>)}
          {!shown.length ? <div className="px-4 py-6 text-center text-[12.5px] text-[var(--gray2)]">No {plural} match your search.</div> : null}
        </ListCard>
      )}
      {rm ? <ConfirmSheet title={`Remove ${titleOf(rm)}?`} body={`It comes off this client's ${plural} and out of the claim pickers. Claims already built keep it.`} confirmLabel="Remove" busy={rmBusy} onConfirm={doRemove} onCancel={() => setRm(null)} /> : null}
    </div>
  );
}

const low = (s) => String(s || "").trim().toLowerCase();
const npiOrName = (c) => (c.npi ? `npi:${c.npi}` : low(c.name) ? `name:${low(c.name)}` : "");

export function MasterStep({ eyebrow, title, lede, children }) {
  const pc = usePsClient();
  if (pc.isLoading) return <><ScreenHead eyebrow={eyebrow} title={title} /><Skeletons /></>;
  return (
    <div className="fade-up">
      <ScreenHead eyebrow={eyebrow} title={title} lede={lede} />
      {!pc.clients.length ? <Empty>Add a client first.</Empty> : <><ClientPicker {...pc} />{pc.cid ? children(pc.cid) : null}</>}
    </div>
  );
}

export function LocationsMaster({ cid }) {
  return <MasterList entity="service_locations" cid={cid} singular="location" plural="locations" testid="ps-loc" enrich headerNote="from your claims and added by you"
    fields={[{ k: "npi", label: "NPI", npi: true }, { k: "name", label: "Location name", required: true }, { k: "pos", label: "Place-of-service code", placeholder: "e.g. 11", upper: true }, { k: "address", label: "Address", wide: true }]}
    titleOf={(r) => titleCase(r.name || "") || "Unnamed location"}
    metaOf={(r) => [posText(r.place_of_service), r.npi ? `NPI ${r.npi}` : "No NPI", fmtAddr(r.address) ? titleCase(fmtAddr(r.address)) : "", r.address && r.address.telephone ? r.address.telephone : "", r.source === "claims" ? "from claims" : ""].filter(Boolean).join(" · ")}
    toRecord={(c) => ({ npi: c.npi || null, name: c.name, place_of_service: (c.pos || "").trim() || null, address: c.address ? { address_1: c.address } : null })}
    fromRecord={(r) => ({ npi: r.npi || "", name: r.name || "", pos: r.place_of_service || "", address: fmtAddr(r.address) })} dupKey={npiOrName} />;
}
// Rendering providers (NM1*82): the source list for the claim form's rendering picker and the Billing relationship builder (API guide §7.1).
export function RenderingMaster({ cid }) {
  return <MasterList entity="rendering_providers" cid={cid} singular="rendering provider" plural="rendering providers" testid="ps-rend" enrich headerNote="from your claims, added by you, and linked in Providers · NPI"
    fields={[{ k: "npi", label: "NPI", npi: true, required: true }, { k: "name", label: "Name", required: true }]}
    titleOf={(r) => titleCase(r.name || "") || "Unnamed provider"} metaOf={(r) => [`NPI ${r.npi || "—"}`, r.taxonomy_desc, r.entity_type].filter(Boolean).join(" · ")}
    toRecord={(c) => ({ npi: c.npi, name: c.name })} fromRecord={(r) => ({ npi: r.npi || "", name: r.name || "" })} dupKey={npiOrName} />;
}
export function PayersMaster({ cid }) {
  return <MasterList entity="payers" cid={cid} singular="payer" plural="payers" testid="ps-pay"
    fields={[{ k: "name", label: "Payer name", required: true }, { k: "payer_id", label: "Payer ID", required: true, upper: true }]}
    titleOf={(r) => r.name || "Unnamed payer"} metaOf={(r) => `Payer ID ${r.payer_id || "—"}`}
    toRecord={(c) => ({ name: c.name, payer_id: c.payer_id })} fromRecord={(r) => ({ name: r.name || "", payer_id: r.payer_id || "" })}
    dupKey={(c) => (c.payer_id ? `id:${low(c.payer_id)}` : low(c.name))} />;
}
export function ReferringMaster({ cid }) {
  return <MasterList entity="referring_physicians" cid={cid} singular="referring physician" plural="referring physicians" testid="ps-ref" enrich
    fields={[{ k: "npi", label: "NPI", npi: true, required: true }, { k: "name", label: "Name", required: true }]}
    titleOf={(r) => titleCase(r.name || "") || "Unnamed physician"} metaOf={(r) => `NPI ${r.npi || "—"}`}
    toRecord={(c) => ({ npi: c.npi, name: c.name })} fromRecord={(r) => ({ npi: r.npi || "", name: r.name || "" })} dupKey={npiOrName} />;
}
